import { Box, Skeleton, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BodyText } from '@/components/BodyText';
import { FailureSubjectKind, useGetAssetFailureDiagnostics } from '@/hooks/api/assets/useGetAssetFailureDiagnostics';
import { TraceIdRow } from './TraceIdRow';

const Panel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  margin: '0 8px 8px 8px',
  padding: '8px 10px',
  borderRadius: '6px',
  backgroundColor: alpha(theme.palette.text.primary, 0.04),
}));

const STATUS_MESSAGES: Record<string, string> = {
  'not-found': 'No log entry found for this failure. It may be older than we keep logs for.',
  unavailable: 'Log search is not configured for this environment.',
};

export const FailureDiagnostics = ({ assetId, kind }: { assetId: string; kind?: FailureSubjectKind }) => {
  const { data, isLoading, isError } = useGetAssetFailureDiagnostics({ assetId, kind, enabled: true });

  if (isLoading) {
    return (
      <Panel>
        <Skeleton variant='text' width='60%' />
      </Panel>
    );
  }

  if (isError || !data) {
    return (
      <Panel>
        <BodyText color='text.secondary' fontSize='0.8125rem'>
          Could not search the logs for this failure.
        </BodyText>
      </Panel>
    );
  }

  if (data.status !== 'found') {
    return (
      <Panel>
        <BodyText color='text.secondary' fontSize='0.8125rem' data-testid='failure-diagnostics-status'>
          {STATUS_MESSAGES[data.status]}
        </BodyText>
      </Panel>
    );
  }

  return (
    <Panel data-testid='failure-diagnostics'>
      <BodyText fontSize='0.8125rem'>{data.summary}</BodyText>
      {data.kind === 'internal' && (
        <BodyText color='text.secondary' fontSize='0.75rem'>
          Nothing to fix on your end. Send the trace ID to support.
        </BodyText>
      )}
      {data.traceId && <TraceIdRow traceId={data.traceId} />}
    </Panel>
  );
};
